/**
 * Authentication Service for v2 Dashboard
 *
 * SECURITY: Uses HttpOnly cookies for token storage (set by server).
 * No tokens are stored in localStorage - they're inaccessible to JavaScript.
 *
 * Authentication flow (BetterAuth):
 * 1. LoginPage shows email+password or magic-link form
 * 2. On success, server sets session cookies
 * 3. This service manages session state and refresh scheduling
 */

import { logger } from '../../utils/logger';

/**
 * Thrown when a network-level error prevents reaching the server
 * (CORS blocked, DNS failure, server down, etc.).
 * Distinct from auth rejection (401) so callers can show
 * "server unavailable" instead of redirecting to login.
 */
export class ServerUnreachableError extends Error {
  constructor(cause?: unknown) {
    super('Server unreachable');
    this.name = 'ServerUnreachableError';
    this.cause = cause;
  }
}

// Environment configuration
// In dev, use empty string to go through Vite proxy (same-origin cookies)
// In prod, use the full URL
const VB_SERVER_URL = import.meta.env.PROD ? import.meta.env.VITE_VB_SERVER_URL || '' : '';

/**
 * Extract a safe user-facing error message from a server response.
 * For 4xx errors, the server message is intentional and safe to display.
 * For 5xx errors, use a generic fallback to avoid leaking server internals.
 */
function safeErrorMessage(
  status: number,
  serverMessage: string | undefined,
  fallback: string
): string {
  if (status >= 500) return fallback;
  return serverMessage || fallback;
}

/** Valid org-level roles */
export type OrgRole = 'admin' | 'editor' | 'viewer';

// Organization type from /me endpoint
export interface AuthOrg {
  org_id: string;
  name: string;
  image: string | null;
  label: string | null;
  type: string | null;
  status: string | null;
  role: OrgRole;
  permissions: string[];
  send_allowed: boolean; // deprecated: use permissions array
  agent_entitlements?: string[];
  /**
   * IANA timezone (e.g. "America/Los_Angeles"). Optional during the
   * vb-server-adds-column → vb-dashboard-consumes-it deploy ordering
   * window. Components should fall back to 'UTC' when absent.
   */
  timezone?: string;
}

// User type for the app
export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  image: string | null;
  role: string;
  orgIds: string[];
  orgs: Record<string, AuthOrg>;
}

// Session storage keys (for non-sensitive data only)
export const USER_KEY = 'vb_user';
export const TOKEN_EXPIRY_KEY = 'vb_token_expiry'; // Store expiry time to schedule refresh

// In-memory WS token cache (never stored in session/local storage)
let cachedWsToken: string | null = null;
let cachedWsTokenExpiry = 0;
const WS_TOKEN_BUFFER_MS = 30 * 1000; // Keep token valid for at least 30 seconds

// Global refresh state to prevent multiple simultaneous refresh attempts
let refreshInProgress: Promise<boolean> | null = null;
let lastRefreshFailure = 0;
const REFRESH_COOLDOWN_MS = 5000; // Wait 5 seconds after a failed refresh before retrying

// Track when auth is definitively invalid (401 on refresh) - stops all reconnect attempts
let authInvalidated = false;

// Callbacks to notify when auth is recovered
const authRecoveryCallbacks: Array<() => void> = [];

/**
 * Sign out and clear session.
 * Returns null (no external redirect needed).
 */
export async function signOut(): Promise<void> {
  logger.info('[auth] Signing out');

  // Call logout endpoint (server will clear cookies for both providers)
  try {
    const response = await fetch(`${VB_SERVER_URL}/v1/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });
    logger.info('[auth] Logout API response:', response.status);
  } catch (err) {
    logger.warn('[auth] Logout API call failed:', err);
  }

  // Clear session storage
  sessionStorage.removeItem(USER_KEY);
  sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
  sessionStorage.removeItem('auth_return_to');
  sessionStorage.removeItem('auth_provider');
  sessionStorage.removeItem('vb_current_org_id');
  logger.info('[auth] Session storage cleared');

  // Reset refresh/token fetch state
  resetAuthInvalidation();
  clearWsToken();

  logger.info('[auth] Logout complete');
}

/**
 * Check if user appears to be authenticated
 * Note: This checks session storage, not the actual cookie (which is HttpOnly)
 * The server will validate the actual token on each request
 */
export function getToken(): string | null {
  // We can't access the HttpOnly cookie directly
  // Check if we have user data as a proxy for being logged in
  const user = getUser();
  const expiry = sessionStorage.getItem(TOKEN_EXPIRY_KEY);

  if (!user || !expiry) return null;

  // Check if token appears expired
  if (Date.now() > parseInt(expiry, 10)) {
    return null;
  }

  // Return a placeholder - actual auth is validated server-side
  return 'cookie-auth';
}

/**
 * Get stored user
 */
export function getUser(): AuthUser | null {
  const userStr = sessionStorage.getItem(USER_KEY);
  if (!userStr) return null;

  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return !!getUser() && !!sessionStorage.getItem(TOKEN_EXPIRY_KEY);
}

/**
 * Check if auth has been invalidated (401 on refresh)
 * When true, all reconnect attempts should stop until user re-authenticates
 */
export function isAuthInvalidated(): boolean {
  return authInvalidated;
}

/**
 * Reset auth invalidation state (called on successful login or sign out)
 */
export function resetAuthInvalidation(): void {
  const wasInvalidated = authInvalidated;
  authInvalidated = false;
  lastRefreshFailure = 0;

  // Notify subscribers that auth has recovered
  if (wasInvalidated) {
    logger.info('[auth] Auth invalidation reset, notifying subscribers');
    authRecoveryCallbacks.forEach((cb) => cb());
  }
}

/**
 * Subscribe to auth recovery events
 * Called when auth transitions from invalidated to valid
 */
export function onAuthRecovery(callback: () => void): () => void {
  authRecoveryCallbacks.push(callback);
  return () => {
    const index = authRecoveryCallbacks.indexOf(callback);
    if (index > -1) authRecoveryCallbacks.splice(index, 1);
  };
}

/**
 * Attempt to recover auth after invalidation
 * Called when tab becomes visible or user interacts
 */
export async function attemptAuthRecovery(): Promise<boolean> {
  if (!authInvalidated) {
    return true; // Already valid
  }

  logger.info('[auth] Attempting auth recovery...');

  // Temporarily allow refresh attempt
  const savedInvalidated = authInvalidated;
  authInvalidated = false;
  lastRefreshFailure = 0;

  try {
    // Try to fetch current user (will attempt refresh if needed)
    const user = await fetchCurrentUser();
    if (user) {
      logger.info('[auth] Auth recovered successfully');
      // Notify subscribers
      authRecoveryCallbacks.forEach((cb) => cb());
      return true;
    }
  } catch (err) {
    logger.debug('[auth] Auth recovery failed:', err);
  }

  // Recovery failed, restore invalidated state
  authInvalidated = savedInvalidated;
  return false;
}

// Auto-recover when tab becomes visible
if (typeof window !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && authInvalidated) {
      logger.debug('[auth] Tab visible, attempting auth recovery');
      attemptAuthRecovery();
    }
  });
}

/**
 * Refresh the session.
 *
 * GET /v1/auth/me (session auto-refreshes server-side on access)
 *
 * Uses a global lock to prevent multiple simultaneous refresh attempts,
 * and a cooldown period after failures to prevent retry storms.
 */
export async function refreshToken(): Promise<boolean> {
  // If auth is invalidated (401 on refresh), don't retry at all
  if (authInvalidated) {
    logger.debug('[auth] Skipping refresh - auth is invalidated');
    return false;
  }

  // If we recently failed a refresh, don't retry yet (prevents retry storms)
  if (lastRefreshFailure > 0 && Date.now() - lastRefreshFailure < REFRESH_COOLDOWN_MS) {
    logger.debug('[auth] Skipping refresh - in cooldown period');
    return false;
  }

  // If a refresh is already in progress, wait for it instead of starting another
  if (refreshInProgress) {
    logger.debug('[auth] Refresh already in progress, waiting...');
    return refreshInProgress;
  }

  // Start the refresh and store the promise so others can wait on it
  refreshInProgress = (async () => {
    try {
      // Sessions auto-refresh server-side.
      // Just verify the session is still valid by calling /me.
      logger.debug('[auth] Checking session validity');
      const response = await fetch(`${VB_SERVER_URL}/v1/auth/me`, {
        credentials: 'include'
      });

      if (!response.ok) {
        logger.warn('[auth] Session check failed:', response.status);
        lastRefreshFailure = Date.now();
        if (response.status >= 500) {
          // Server is down — don't clear session, don't mark auth as invalid.
          // AuthProvider will show "server unavailable" via fetchCurrentUser throw.
          logger.warn('[auth] Server error during session check, preserving session');
          return false;
        }
        sessionStorage.removeItem(USER_KEY);
        sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
        if (response.status === 401) {
          authInvalidated = true;
          logger.warn('[auth] Auth invalidated - user must re-authenticate');
        }
        return false;
      }

      // Session is still valid — extend local expiry tracker
      const expiryTime = Date.now() + 24 * 60 * 60 * 1000; // 24h (matches session config)
      sessionStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
      logger.info('[auth] Session verified');
      lastRefreshFailure = 0;
      authInvalidated = false;
      return true;
    } catch (err) {
      logger.error('[auth] Token refresh error:', err);
      lastRefreshFailure = Date.now();
      return false;
    } finally {
      refreshInProgress = null;
    }
  })();

  return refreshInProgress;
}

/** Parse /v1/auth/me response data into AuthUser and store in session */
function parseUserResponse(data: Record<string, unknown>): AuthUser {
  const user: AuthUser = {
    id: data.id as string,
    email: data.email as string,
    name: data.name as string,
    image: data.image as string | null,
    role: data.role as string,
    orgIds: (data.orgIds as string[]) || [],
    orgs: (data.orgs as Record<string, AuthOrg>) || {}
  };
  sessionStorage.setItem(USER_KEY, JSON.stringify(user));
  return user;
}

/**
 * Get current user info from server
 */
export async function fetchCurrentUser(): Promise<AuthUser | null> {
  try {
    const response = await fetch(`${VB_SERVER_URL}/v1/auth/me`, {
      credentials: 'include' // Important: cookies sent automatically
    });

    if (!response.ok) {
      // 5xx = server is down/broken (includes Vite proxy 500 when server is unreachable).
      // Throw so AuthProvider can show "server unavailable" instead of redirecting to login.
      if (response.status >= 500) {
        throw new ServerUnreachableError(new Error(`Server returned ${response.status}`));
      }

      if (response.status === 401) {
        // Try to refresh token (one attempt only to prevent infinite recursion)
        const refreshed = await refreshToken();
        if (refreshed) {
          const retryResponse = await fetch(`${VB_SERVER_URL}/v1/auth/me`, {
            credentials: 'include'
          });
          if (retryResponse.ok) {
            const retryResult = await retryResponse.json();
            if (retryResult.success && retryResult.data) {
              return parseUserResponse(retryResult.data);
            }
          }
        }
      }
      return null;
    }

    const result = await response.json();

    if (result.success && result.data) {
      return parseUserResponse(result.data);
    }

    return null;
  } catch (err) {
    // Already a ServerUnreachableError (from 5xx check above) — rethrow as-is
    if (err instanceof ServerUnreachableError) throw err;
    // Network-level failure (CORS blocked, server down, DNS, etc.)
    // Throw so callers can distinguish "server unreachable" from "unauthorized"
    logger.error('[auth] Failed to fetch current user:', err);
    throw new ServerUnreachableError(err);
  }
}

/**
 * Get token expiry time in seconds from now
 * Returns -1 if no expiry or expired
 */
export function getTokenExpirySeconds(): number {
  const expiry = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiry) return -1;

  const expiryTime = parseInt(expiry, 10);
  const now = Date.now();

  if (now >= expiryTime) return -1;

  return Math.floor((expiryTime - now) / 1000);
}

/**
 * Check if token is expired or about to expire
 */
export function isTokenExpired(bufferSeconds = 60): boolean {
  const secondsRemaining = getTokenExpirySeconds();
  return secondsRemaining < bufferSeconds;
}

/**
 * Subscribe to auth state changes
 * Returns unsubscribe function
 */
export function onAuthStateChanged(callback: (user: AuthUser | null) => void): () => void {
  // Listen for storage changes (cross-tab sync)
  // Note: initial state is handled by AuthProvider.initAuth, not here.
  const handleStorageChange = (e: StorageEvent) => {
    if (e.key === USER_KEY || e.key === TOKEN_EXPIRY_KEY) {
      callback(getUser());
    }
  };

  window.addEventListener('storage', handleStorageChange);

  return () => {
    window.removeEventListener('storage', handleStorageChange);
  };
}

// Global WS token fetch state to prevent multiple simultaneous requests
let wsTokenFetchInProgress: Promise<string | null> | null = null;

/**
 * Fetch a short-lived WebSocket token
 * Used for WebSocket connections since they can't use HttpOnly cookies
 *
 * Uses deduplication to prevent multiple simultaneous fetch attempts.
 */
export async function fetchWsToken(allowRefresh = true): Promise<string | null> {
  // If auth is invalidated, don't even try
  if (authInvalidated) {
    logger.debug('[auth] Skipping WS token fetch - auth is invalidated');
    return null;
  }

  // Return cached token if still valid
  if (cachedWsToken && Date.now() + WS_TOKEN_BUFFER_MS < cachedWsTokenExpiry) {
    return cachedWsToken;
  }

  // If a fetch is already in progress, wait for it
  if (wsTokenFetchInProgress) {
    logger.debug('[auth] WS token fetch already in progress, waiting...');
    return wsTokenFetchInProgress;
  }

  // Start the fetch and store the promise
  wsTokenFetchInProgress = (async () => {
    try {
      // Check cache again in case another call populated it
      if (cachedWsToken && Date.now() + WS_TOKEN_BUFFER_MS < cachedWsTokenExpiry) {
        return cachedWsToken;
      }

      // Fetch new token from server
      const response = await fetch(`${VB_SERVER_URL}/v1/auth/ws-token`, {
        credentials: 'include'
      });

      if (!response.ok) {
        logger.warn('[auth] Failed to fetch WS token:', response.status);
        clearWsToken();

        if (response.status === 401 && allowRefresh) {
          const refreshed = await refreshToken();
          if (refreshed) {
            // Clear the in-progress flag before recursive call
            wsTokenFetchInProgress = null;
            return fetchWsToken(false);
          }
        }

        return null;
      }

      const result = await response.json();

      if (result.success && result.data?.token) {
        const token = result.data.token;
        const expiresIn = result.data.expires_in || 300; // Default 5 minutes
        cachedWsToken = token;
        cachedWsTokenExpiry = Date.now() + expiresIn * 1000;

        logger.debug('[auth] Fetched new WS token, expires in', expiresIn, 'seconds');
        return token;
      }

      return null;
    } catch (err) {
      logger.error('[auth] Failed to fetch WS token:', err);
      return null;
    } finally {
      wsTokenFetchInProgress = null;
    }
  })();

  return wsTokenFetchInProgress;
}

/**
 * Clear WebSocket token (called on logout)
 */
export function clearWsToken(): void {
  cachedWsToken = null;
  cachedWsTokenExpiry = 0;
}

// ─── Better Auth Sign-In ─────────────────────────────────────────────────────

/**
 * Sign in via Better Auth email+password.
 * Posts directly to the Better Auth endpoint, which sets session cookies.
 *
 * @returns The authenticated user, or null on failure.
 */
export async function betterAuthSignIn(
  email: string,
  password: string
): Promise<{ user: AuthUser | null; error: string | null }> {
  try {
    // Better Auth sign-in endpoint (mounted at /api/auth in vb-server)
    const response = await fetch(`${VB_SERVER_URL}/api/auth/sign-in/email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      if (response.status === 429) {
        return {
          user: null,
          error: 'Too many sign-in attempts. Please wait a moment and try again.'
        };
      }
      const data = await response.json().catch(() => ({}));
      const fallback = response.status === 401 ? 'Invalid email or password' : 'Sign in failed';
      return { user: null, error: safeErrorMessage(response.status, data?.message, fallback) };
    }

    // Better Auth sets session cookies. Now fetch user info from our /me endpoint.
    const user = await fetchCurrentUser();
    if (user) {
      // Set a long expiry for the session tracker (BA sessions are 24h, auto-refreshed)
      const expiryTime = Date.now() + 24 * 60 * 60 * 1000;
      sessionStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
      resetAuthInvalidation();
    }

    return { user, error: user ? null : 'Failed to fetch user after sign-in' };
  } catch (err) {
    logger.error('[auth] Better Auth sign-in error:', err);
    return { user: null, error: 'Network error. Please try again.' };
  }
}

/**
 * Sign up via Better Auth email+password.
 *
 * @returns The authenticated user, or null on failure.
 */
export async function betterAuthSignUp(
  email: string,
  password: string,
  name: string
): Promise<{ user: AuthUser | null; error: string | null; requiresVerification?: boolean }> {
  try {
    const response = await fetch(`${VB_SERVER_URL}/api/auth/sign-up/email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, name })
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      return {
        user: null,
        error: safeErrorMessage(response.status, data?.message, 'Sign up failed')
      };
    }

    // Parse the signup response to check if user was pre-verified (invited users)
    const signupData = await response.json().catch(() => ({}));

    // Try to fetch user — if email verification is required, no session exists yet
    const user = await fetchCurrentUser();
    if (user) {
      const expiryTime = Date.now() + 24 * 60 * 60 * 1000;
      sessionStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
      resetAuthInvalidation();
      return { user, error: null };
    }

    // Invited users are pre-verified — auto-sign them in with their credentials
    if (signupData?.user?.emailVerified) {
      logger.info('[auth] User was pre-verified (invited), auto-signing in');
      const signInResult = await betterAuthSignIn(email, password);
      if (signInResult.user) {
        return { user: signInResult.user, error: null };
      }
    }

    // Sign-up succeeded but no session — email verification required
    return { user: null, error: null, requiresVerification: true };
  } catch (err) {
    logger.error('[auth] Better Auth sign-up error:', err);
    return { user: null, error: 'Network error. Please try again.' };
  }
}

/**
 * Request a magic link via Better Auth.
 */
export async function betterAuthMagicLink(
  email: string
): Promise<{ success: boolean; error: string | null }> {
  try {
    const response = await fetch(`${VB_SERVER_URL}/api/auth/sign-in/magic-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email })
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      return {
        success: false,
        error: safeErrorMessage(response.status, data?.message, 'Failed to send magic link')
      };
    }

    return { success: true, error: null };
  } catch (err) {
    logger.error('[auth] Magic link error:', err);
    return { success: false, error: 'Network error. Please try again.' };
  }
}

/**
 * Request a password reset email via Better Auth.
 */
export async function betterAuthForgotPassword(
  email: string
): Promise<{ success: boolean; error: string | null }> {
  try {
    const response = await fetch(`${VB_SERVER_URL}/api/auth/request-password-reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email })
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      return {
        success: false,
        error: safeErrorMessage(response.status, data?.message, 'Failed to send reset email')
      };
    }

    return { success: true, error: null };
  } catch (err) {
    logger.error('[auth] Forgot password error:', err);
    return { success: false, error: 'Network error. Please try again.' };
  }
}

/**
 * Reset password using a token from the reset email.
 */
export async function betterAuthResetPassword(
  newPassword: string,
  token: string
): Promise<{ success: boolean; error: string | null }> {
  try {
    const response = await fetch(`${VB_SERVER_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ newPassword, token })
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      return {
        success: false,
        error: safeErrorMessage(response.status, data?.message, 'Failed to reset password')
      };
    }

    return { success: true, error: null };
  } catch (err) {
    logger.error('[auth] Reset password error:', err);
    return { success: false, error: 'Network error. Please try again.' };
  }
}

// ─── Password Management ─────────────────────────────────────────────────────

/**
 * Check if the current user has a password (credential account).
 * Returns false for magic-link-only users.
 */
export async function betterAuthHasPassword(): Promise<boolean> {
  try {
    const response = await fetch(`${VB_SERVER_URL}/v1/auth/has-password`, {
      credentials: 'include'
    });

    if (!response.ok) return false;

    const result = await response.json();
    return result.success && result.data?.hasPassword === true;
  } catch (err) {
    logger.error('[auth] has-password check error:', err);
    return false;
  }
}

/**
 * Set a password for a user who doesn't have one (magic link signup).
 */
export async function betterAuthSetPassword(
  newPassword: string
): Promise<{ success: boolean; error: string | null }> {
  try {
    const response = await fetch(`${VB_SERVER_URL}/v1/auth/set-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ newPassword })
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      return {
        success: false,
        error: safeErrorMessage(response.status, data?.message, 'Failed to set password')
      };
    }

    return { success: true, error: null };
  } catch (err) {
    logger.error('[auth] Set password error:', err);
    return { success: false, error: 'Network error. Please try again.' };
  }
}

/**
 * Change password for a user who already has one.
 * Uses Better Auth's built-in change-password endpoint.
 */
export async function betterAuthChangePassword(
  currentPassword: string,
  newPassword: string
): Promise<{ success: boolean; error: string | null }> {
  try {
    const response = await fetch(`${VB_SERVER_URL}/api/auth/change-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ currentPassword, newPassword, revokeOtherSessions: true })
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      return {
        success: false,
        error: safeErrorMessage(response.status, data?.message, 'Failed to change password')
      };
    }

    return { success: true, error: null };
  } catch (err) {
    logger.error('[auth] Change password error:', err);
    return { success: false, error: 'Network error. Please try again.' };
  }
}

/**
 * Verify a magic link token via Better Auth.
 * Called from the dashboard's /v2/verify-magic-link page instead of
 * redirecting through the API server.
 */
export async function betterAuthVerifyMagicLink(
  token: string
): Promise<{ success: boolean; error: string | null }> {
  try {
    const response = await fetch(
      `${VB_SERVER_URL}/api/auth/magic-link/verify?token=${encodeURIComponent(token)}`,
      { method: 'GET', credentials: 'include' }
    );

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      return {
        success: false,
        error: safeErrorMessage(response.status, data?.message, 'Invalid or expired magic link.')
      };
    }

    // Session cookies are now set. Fetch user info and bootstrap sessionStorage
    // so AuthProvider recognises the session on redirect.
    const user = await fetchCurrentUser();
    if (user) {
      const expiryTime = Date.now() + 24 * 60 * 60 * 1000;
      sessionStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
      resetAuthInvalidation();
    }

    return { success: true, error: null };
  } catch (err) {
    logger.error('[auth] Magic link verify error:', err);
    return { success: false, error: 'Network error. Please try again.' };
  }
}

/**
 * Verify an email verification token via Better Auth.
 * Called from the dashboard's /v2/verify-email page instead of
 * redirecting through the API server.
 */
export async function betterAuthVerifyEmail(
  token: string
): Promise<{ success: boolean; error: string | null }> {
  try {
    const response = await fetch(
      `${VB_SERVER_URL}/api/auth/verify-email?token=${encodeURIComponent(token)}`,
      { method: 'GET', credentials: 'include' }
    );

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      return {
        success: false,
        error: safeErrorMessage(
          response.status,
          data?.message,
          'Invalid or expired verification link.'
        )
      };
    }

    // Session cookies are now set. Fetch user info and bootstrap sessionStorage
    // so AuthProvider recognises the session on redirect.
    const user = await fetchCurrentUser();
    if (user) {
      const expiryTime = Date.now() + 24 * 60 * 60 * 1000;
      sessionStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
      resetAuthInvalidation();
    }

    return { success: true, error: null };
  } catch (err) {
    logger.error('[auth] Email verify error:', err);
    return { success: false, error: 'Network error. Please try again.' };
  }
}
